import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Layers, Lock, Pencil, Plus, Trash2, Save, X, History, ShieldAlert, CalendarClock, CheckCircle2,
} from "lucide-react";
import { extractErrorMessage } from "@/lib/utils";

const fmtINR = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const emptyRow = () => ({
  id: `new-${Math.random().toString(36).slice(2, 9)}`,
  min_invoice_value: 0,
  max_invoice_value: 0,
  range_label: "",
  wellcore_discount: "",
  other_brands_discount: "",
  add_dynamite: "",
  add_omega: "",
  add_osmo: "",
});

const StatusPill = ({ status }) => {
  const map = {
    current: { label: "CURRENT", cls: "bg-[#32B4AC]/10 text-[#0d7a73] border-[#32B4AC]/40", Icon: CheckCircle2 },
    locked: { label: "LOCKED", cls: "bg-slate-100 text-slate-600 border-slate-300", Icon: Lock },
    future: { label: "FUTURE", cls: "bg-amber-50 text-amber-700 border-amber-300", Icon: CalendarClock },
  };
  const { label, cls, Icon } = map[status] || map.future;
  return (
    <Badge variant="outline" className={`gap-1 ${cls}`} data-testid={`slab-status-${status}`}>
      <Icon className="h-3 w-3" /> {label}
    </Badge>
  );
};

export default function SlabIntelligence() {
  const [quarters, setQuarters] = useState([]);
  const [selectedQid, setSelectedQid] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftRows, setDraftRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const currentStatus = detail?.status;

  const loadQuarters = useCallback(async () => {
    const res = await axios.get(`${API}/slab-intelligence/quarters`, { withCredentials: true });
    const qs = res.data?.quarters || [];
    setQuarters(qs);
    return qs;
  }, []);

  const loadDetail = useCallback(async (qid) => {
    setLoading(true);
    setEditing(false);
    try {
      const res = await axios.get(`${API}/slab-intelligence/quarters/${qid}`, { withCredentials: true });
      setDetail(res.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load quarter"));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const qs = await loadQuarters();
        const cur = qs.find((q) => q.status === "current") || qs[0];
        if (cur) {
          setSelectedQid(cur.quarter_id);
          await loadDetail(cur.quarter_id);
        } else {
          setLoading(false);
        }
      } catch (e) {
        toast.error(extractErrorMessage(e, "Failed to load slab quarters"));
        setLoading(false);
      }
    })();
  }, [loadQuarters, loadDetail]);

  const onSelectQuarter = async (qid) => {
    setSelectedQid(qid);
    await loadDetail(qid);
  };

  const startEdit = () => {
    setDraftRows((detail?.slabs || []).map((r) => ({ ...r })));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraftRows([]);
  };

  const updateCell = (idx, field, value) => {
    setDraftRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setDraftRows((prev) => [...prev, emptyRow()]);
  const deleteRow = (idx) => setDraftRows((prev) => prev.filter((_, i) => i !== idx));

  const doSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const payload = {
        slabs: draftRows.map((r) => ({
          id: String(r.id || "").startsWith("new-") ? undefined : r.id,
          min_invoice_value: Number(r.min_invoice_value) || 0,
          max_invoice_value: Number(r.max_invoice_value) || 0,
          range_label: r.range_label || "",
          wellcore_discount: r.wellcore_discount || "",
          other_brands_discount: r.other_brands_discount || "",
          add_dynamite: r.add_dynamite || "",
          add_omega: r.add_omega || "",
          add_osmo: r.add_osmo || "",
        })),
      };
      const res = await axios.put(`${API}/slab-intelligence/quarters/${selectedQid}`, payload, { withCredentials: true });
      setDetail(res.data);
      setEditing(false);
      setDraftRows([]);
      await loadQuarters();
      toast.success(`Saved. ${res.data?.changes_recorded || 0} change(s) recorded.`);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to save slab structure"));
    } finally {
      setSaving(false);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    try {
      const res = await axios.get(`${API}/slab-intelligence/quarters/${selectedQid}/audit`, { withCredentials: true });
      setAudit(res.data?.audit || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load history"));
      setAudit([]);
    }
  };

  const rows = editing ? draftRows : (detail?.slabs || []);
  const canEdit = Boolean(detail?.can_edit);
  const isLocked = currentStatus === "locked";

  const COLS = [
    "Min Invoice Value", "Max Invoice Value", "Invoice Value Range",
    "New Discount % (Wellcore)", "New Discount % (Other Brands)",
    "Additional % – Dynamite", "Additional % – Omega", "Additional % – OSMO",
  ];

  return (
    <div className="space-y-5" data-testid="slab-intelligence-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap border-b border-[#E8E8E8] pb-4">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F] flex items-center gap-2" data-testid="slab-intelligence-title">
            <Layers className="h-5 w-5 text-[#32B4AC]" />
            Slab Intelligence
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Quarter-wise discount slab master reference. Current &amp; future quarters are Admin-editable; past quarters are permanently locked.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedQid} onValueChange={onSelectQuarter}>
            <SelectTrigger className="w-[280px] h-9" data-testid="slab-quarter-select">
              <SelectValue placeholder="Select quarter" />
            </SelectTrigger>
            <SelectContent>
              {quarters.map((q) => (
                <SelectItem key={q.quarter_id} value={q.quarter_id} data-testid={`slab-quarter-option-${q.quarter_id}`}>
                  {q.label} — {q.status.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 border-[#E8E8E8]" onClick={openAudit} data-testid="slab-history-button">
            <History className="h-4 w-4 mr-1.5" /> History
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#32B4AC]" />
        </div>
      ) : !detail ? (
        <div className="text-center py-16 text-muted-foreground">No quarter selected.</div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <CardTitle className="text-lg font-semibold">{detail.label}</CardTitle>
                <StatusPill status={detail.status} />
              </div>
              <div className="flex items-center gap-2">
                {!editing && canEdit && (
                  <Button size="sm" className="h-8 bg-[#32B4AC] hover:bg-[#2A9A93] text-white" onClick={startEdit} data-testid="slab-edit-button">
                    <Pencil className="h-4 w-4 mr-1.5" /> Edit
                  </Button>
                )}
                {editing && (
                  <>
                    <Button size="sm" variant="outline" className="h-8" onClick={addRow} data-testid="slab-add-row-button">
                      <Plus className="h-4 w-4 mr-1.5" /> Add Row
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={cancelEdit} disabled={saving} data-testid="slab-cancel-button">
                      <X className="h-4 w-4 mr-1.5" /> Cancel
                    </Button>
                    <Button size="sm" className="h-8 bg-[#32B4AC] hover:bg-[#2A9A93] text-white" onClick={() => setConfirmOpen(true)} disabled={saving} data-testid="slab-save-button">
                      <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Changes"}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {isLocked && (
              <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-1.5 w-fit" data-testid="slab-locked-banner">
                <Lock className="h-4 w-4" /> Locked – Read Only. This quarter has ended and its structure can no longer be changed.
              </div>
            )}
            {detail.status === "future" && !editing && (
              <div className="mt-2 inline-flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 w-fit">
                <CalendarClock className="h-4 w-4" /> Future quarter. It becomes the active quarter automatically on {detail.start_date}.
              </div>
            )}
            {detail.updated_at && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Last updated {new Date(detail.updated_at).toLocaleString("en-IN")}
                {detail.updated_by?.name ? ` by ${detail.updated_by.name}` : ""}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-auto border rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-50 z-10">
                  <TableRow>
                    {COLS.map((c) => (
                      <TableHead key={c} className="font-semibold text-[#0F0F0F] whitespace-nowrap">{c}</TableHead>
                    ))}
                    {editing && <TableHead className="font-semibold w-12"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={editing ? COLS.length + 1 : COLS.length} className="text-center py-10 text-muted-foreground">
                        No slabs configured for this quarter{canEdit ? " yet. Click Edit → Add Row to configure." : "."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r, idx) => (
                      <TableRow key={r.id || idx} data-testid={`slab-row-${idx}`}>
                        {editing ? (
                          <>
                            <TableCell><Input type="number" value={r.min_invoice_value} onChange={(e) => updateCell(idx, "min_invoice_value", e.target.value)} className="h-8 w-28" data-testid={`slab-min-${idx}`} /></TableCell>
                            <TableCell><Input type="number" value={r.max_invoice_value} onChange={(e) => updateCell(idx, "max_invoice_value", e.target.value)} className="h-8 w-28" data-testid={`slab-max-${idx}`} /></TableCell>
                            <TableCell><Input value={r.range_label} onChange={(e) => updateCell(idx, "range_label", e.target.value)} className="h-8 w-44" data-testid={`slab-range-${idx}`} /></TableCell>
                            <TableCell><Input value={r.wellcore_discount} onChange={(e) => updateCell(idx, "wellcore_discount", e.target.value)} className="h-8 w-24" data-testid={`slab-wellcore-${idx}`} /></TableCell>
                            <TableCell><Input value={r.other_brands_discount} onChange={(e) => updateCell(idx, "other_brands_discount", e.target.value)} className="h-8 w-24" data-testid={`slab-other-${idx}`} /></TableCell>
                            <TableCell><Input value={r.add_dynamite} onChange={(e) => updateCell(idx, "add_dynamite", e.target.value)} className="h-8 w-28" data-testid={`slab-dynamite-${idx}`} /></TableCell>
                            <TableCell><Input value={r.add_omega} onChange={(e) => updateCell(idx, "add_omega", e.target.value)} className="h-8 w-28" data-testid={`slab-omega-${idx}`} /></TableCell>
                            <TableCell><Input value={r.add_osmo} onChange={(e) => updateCell(idx, "add_osmo", e.target.value)} className="h-8 w-28" data-testid={`slab-osmo-${idx}`} /></TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => deleteRow(idx)} data-testid={`slab-delete-row-${idx}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="font-mono whitespace-nowrap">{fmtINR(r.min_invoice_value)}</TableCell>
                            <TableCell className="font-mono whitespace-nowrap">{fmtINR(r.max_invoice_value)}</TableCell>
                            <TableCell className="whitespace-nowrap">{r.range_label || "—"}</TableCell>
                            <TableCell className="font-medium">{r.wellcore_discount || "—"}</TableCell>
                            <TableCell className="font-medium">{r.other_brands_discount || "—"}</TableCell>
                            <TableCell>{r.add_dynamite || "—"}</TableCell>
                            <TableCell>{r.add_omega || "—"}</TableCell>
                            <TableCell>{r.add_osmo || "—"}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {!canEdit && !isLocked && detail.status !== "locked" && (
              <p className="mt-3 text-[11px] text-muted-foreground flex items-center gap-1.5">
                <ShieldAlert className="h-3.5 w-3.5" /> You have view-only access. Only Admins can edit slab structures.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="slab-save-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Save slab changes for {detail?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              You are updating the discount slab master for this quarter. The change will be recorded in the audit history. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="slab-save-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#32B4AC] hover:bg-[#2A9A93]" onClick={doSave} data-testid="slab-save-confirm-ok">Save Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit history */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-2xl" data-testid="slab-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-[#32B4AC]" /> Change History — {detail?.label}</DialogTitle>
            <DialogDescription>Every admin edit to this quarter's slab structure, newest first.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto space-y-3">
            {audit.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No changes recorded yet.</p>
            ) : (
              audit.map((a) => (
                <div key={a.id} className="border rounded-lg p-3" data-testid="slab-audit-entry">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <span className="font-medium text-[#0F0F0F]">{a.changed_by?.name || a.changed_by?.email || "Unknown"}</span>
                    <span>{new Date(a.changed_at).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="space-y-1">
                    {(a.changes || []).map((c, i) => (
                      <div key={i} className="text-xs flex flex-wrap items-center gap-1.5">
                        <span className="text-slate-500">{c.row}</span>
                        <span className="font-medium">{c.field}:</span>
                        <span className="text-red-600 line-through">{c.old}</span>
                        <span>→</span>
                        <span className="text-green-700 font-medium">{c.new}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
